import { Box, Button, Checkbox, Icon, Modal, Skeleton, Spinner, Text, Tooltip } from '@sendible/design-system';
import { getAvatarInitials, getColorTone } from '@sendible/design-system/src/components/utils/helpers';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBridgeUser } from '@sendible/shared-state-bridge';
import { EVENTS, publish } from '@sendible/event-manager';
import { useDeleteMessage, useResolveRecipients } from '@sendible/frontendv2/src/models/calendar';
import { PdfThumbnail } from '@sendible/frontendv2/src/components/MediaSystem/components/MediaBelt/PdfThumbnail';
import { MediaPreview } from '@sendible/frontendv2/src/components';
import {
  ActionsArea,
  CampaignTag,
  CampaignsRow,
  ChannelAvatar,
  ChannelItem,
  ChannelList,
  Container,
  ContentArea,
  DeleteActions,
  ErrorBox,
  FailedRecipientsList,
  InfoArea,
  MediaRow,
  MediaThumb,
  MessageText,
  StatusIconWrapper,
  ThumbHighlight,
  ThumbOverflow,
  TopRow,
  WorkspaceAvatar,
} from './index.styles';
import {
  editMessage,
  formatTimeLabelNoTransform,
  fullDateToMonthString,
  getChannelIcon,
  getDeliveryStatusIcon,
  getDeliveryStatusTooltip,
  getCalendarThumbnailUrl,
  getFailureReasonMessage,
  getIsPartialModeSuccess,
  isPdfMedia,
  processTags,
} from '../../utils';
import { useCalendarMediaList, useRenderWithLinks } from '../../hooks';
import { ProgressIndicator } from '../ProgressIndicator';
import { RecipientCard } from '../RecipientCard';
import { useCalendarContext } from '../../context';
import { useSelectionContext } from '../../selectionContext';

export interface Props {
  id: CalendarMessage['id'];
  campaigns?: CalendarCampaign[];
  deliveryStatus: CalendarMessage['deliveryStatus'];
  destinations: CalendarDestination[];
  medias?: CalendarMedia[];
  postAt: string;
  recurrence?: CalendarRecurrence;
  recipients?: CalendarRecipient[];
  status: CalendarStatuses;
  title: string;
  workspace: CalendarWorkspace;
}

export const ListCalendarCard = ({
  id,
  campaigns,
  deliveryStatus,
  destinations,
  medias,
  postAt,
  recurrence,
  recipients,
  status,
  title,
  workspace,
}: Props) => {
  const user = useBridgeUser();
  const { filterParams } = useCalendarContext();
  const { t, i18n } = useTranslation(['calendar', 'common_terms']);
  const deleteMessage = useDeleteMessage();
  const resolveRecipients = useResolveRecipients();
  const { isSelected, selectedMessageIds, toggleMessage } = useSelectionContext();
  const isMessageSelected = isSelected(id);
  const showCheckboxAlways = selectedMessageIds.length > 0;
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [hasHovered, setHasHovered] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState(Boolean(medias?.[0]?.url));
  const imgRef = useRef<HTMLImageElement | null>(null);

  const clockFormat = user?.clockFormat ?? 12;
  const { color, name } = workspace;
  const colorTone = getColorTone(color);
  const isDisabled = status === 'Processing';
  const isPartialModeSuccess = getIsPartialModeSuccess(filterParams);
  const deliveryStatusIcon = getDeliveryStatusIcon(status, !!recurrence, deliveryStatus, isPartialModeSuccess);
  const failedRecipients = (recipients ?? deliveryStatus?.recipients ?? []).filter((r) => r.status !== 'Sent');
  const mediaList = useCalendarMediaList(medias || [], showPreview);

  const TOOLTIP_DELAY = 250;

  const isSentOrFailed = status === 'Sent' || status === 'Failed' || status === 'Bounced';
  const isDraftOrScheduled = status === 'Draft' || status === 'Pending' || status === 'Queued' || status === 'Approved';
  const isRejected = status === 'Rejected';
  const isForApproval = status === 'ForApproval';

  const hasFailureReason = Boolean(deliveryStatus.failureReason);
  const failureReasonMessageKey = getFailureReasonMessage(deliveryStatus.failureReason ?? '');
  const failureReasonMessage = failureReasonMessageKey ? t(failureReasonMessageKey) : '';

  const renderWithLinks = useRenderWithLinks();

  const getStatusTooltip = () =>
    getDeliveryStatusTooltip({
      recurrence,
      deliveryStatus,
      status,
      isPartialModeSuccess,
      isPaused: user?.isPaused,
      t,
      language: i18n.language,
    });

  const triggerEditMessage = useCallback(
    (statusOverride?: CalendarStatuses) => {
      const date = fullDateToMonthString(postAt as CalendarFullDateString);
      const offset = user?.timezone;

      editMessage(statusOverride ?? status, id, date, offset);
    },
    [id, postAt, status, user?.timezone]
  );

  const handleEdit = () => triggerEditMessage();

  const handleRepost = () => {
    const repostStatus = status === 'Failed' || status === 'Bounced' ? 'Sent' : status;

    triggerEditMessage(repostStatus);
  };

  const handleDeleteConfirm = () => {
    deleteMessage.mutate(id);
    setIsDeleteConfirmOpen(false);
  };

  const handleDownload = async (action: string, mediaId: MediaIdType) => {
    if (action !== 'download') return;

    const media = mediaList.find((m) => m.id === mediaId);

    if (!media) return;

    const response = await fetch(media.downloadUrl);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const tempLink = document.createElement('a');

    tempLink.href = blobUrl;
    tempLink.setAttribute('download', media.name);
    tempLink.click();
    tempLink.remove();
    URL.revokeObjectURL(blobUrl);
  };

  const recipientsSection =
    failedRecipients.length > 0 ? (
      <FailedRecipientsList data-testid="list-calendar-card-error-list">
        {failedRecipients.map((r) => (
          <RecipientCard
            key={r.id}
            id={r.id}
            name={r.name}
            networkIcon={getChannelIcon(r.network as MessageChannels)}
            networkUrl={r.avatarUrl}
            errorMessage={r.errorMessage}
            isSuccess={r.status === 'Sent'}
            isResolved={r.isResolved}
            isLoading={resolveRecipients.isPending && (resolveRecipients.variables?.recipientIds?.includes(r.id) ?? false)}
            onResolve={() => resolveRecipients.mutate({ recipientIds: [r.id], messageId: id })}
            onRepost={() =>
              publish(EVENTS.OPEN_COMPOSE, {
                mode: 'repost',
                id,
                date: fullDateToMonthString(postAt as CalendarFullDateString),
                offset: user?.timezone ?? 0,
                recipientId: r.profileId,
              })
            }
            useSummaryVersion
          />
        ))}
      </FailedRecipientsList>
    ) : null;

  return (
    <>
      {isDeleteConfirmOpen && (
        <Modal
          close={() => setIsDeleteConfirmOpen(false)}
          customClass="global-modal"
          headingTitle={t('delete_confirm_title') as string}
          testId="delete-post-modal"
          variation="small"
          ariaCloseLabel={t('close') as string}
        >
          <Box
            orientation="column"
            flex={1}
          >
            <p>{t('delete_confirm_text')}</p>
            <DeleteActions contentJustify="flex-end">
              <Button
                onClick={() => setIsDeleteConfirmOpen(false)}
                label={t('delete_confirm_cancel') as string}
                size={16}
                variation="outline"
                autoFocus
              />
              <Button
                appearance="primary"
                onClick={handleDeleteConfirm}
                label={t('delete_confirm_yes') as string}
                isLoading={deleteMessage.isPending}
                size={16}
                variation="fill"
              />
            </DeleteActions>
          </Box>
        </Modal>
      )}
      <Container
        data-testid="list-calendar-card"
        id={`list-calendar-card-message-${id}`}
        $isDisabled={isDisabled}
        $showCheckboxAlways={showCheckboxAlways}
        onMouseEnter={() => !hasHovered && setHasHovered(true)}
      >
        <Tooltip
          noDecoration
          content={getStatusTooltip()}
          maxWidth
          delay={TOOLTIP_DELAY}
        >
          <InfoArea data-testid="list-calendar-card-info-area">
            <StatusIconWrapper onClick={(e) => e.stopPropagation()}>
              {/* data-export-hide keeps the selection checkbox out of the exported/printed HTML snapshot,
                  same convention as the other UI-only controls excluded by exportCalendarSectionHtml. */}
              <span
                data-export-hide
                style={{ display: 'contents' }}
              >
                <Checkbox
                  ariaLabel={t('select_post') as string}
                  check={() => toggleMessage(id)}
                  initialState={isMessageSelected ? 'checked' : 'unchecked'}
                  isDisabled={isDisabled}
                  size="sm"
                  testId="list-calendar-card-checkbox"
                />
              </span>
              {/* Its own positioning context so the progress ring (absolutely centered on it)
                  tracks the icon as the checkbox slides in and pushes it, instead of staying
                  pinned to where the icon used to sit. */}
              <span className="status-icon">
                {status === 'Processing' ? (
                  <Spinner size="xs" />
                ) : (
                  deliveryStatusIcon && (
                    <Icon
                      name={deliveryStatusIcon.icon}
                      color={deliveryStatusIcon.color}
                      strokeWidth={1.8}
                    />
                  )
                )}
                {deliveryStatusIcon?.progress && (
                  <ProgressIndicator
                    percent={deliveryStatusIcon.progress.percent}
                    type={deliveryStatusIcon.progress.type}
                  />
                )}
              </span>
            </StatusIconWrapper>
            <p>{formatTimeLabelNoTransform(postAt, clockFormat)}</p>
          </InfoArea>
        </Tooltip>

        <ContentArea data-testid="list-calendar-card-content-area">
          <TopRow>
            <ChannelList>
              {destinations.map((d) => {
                const icon = d?.channelType ? getChannelIcon(d.channelType) : undefined;

                return (
                  <ChannelItem
                    key={`${d.channelType}-${d.channelId}`}
                    data-testid="list-calendar-card-channel-item"
                  >
                    {icon && (
                      <ChannelAvatar>
                        <Icon
                          name={icon}
                          strokeWidth={1.6}
                        />
                      </ChannelAvatar>
                    )}
                    <Text
                      variation="body_12"
                      color="brandDarkTint30"
                      maxLines={1}
                    >
                      {d.channelName}
                    </Text>
                  </ChannelItem>
                );
              })}
            </ChannelList>
            <ActionsArea
              className="list-card-actions"
              data-testid="list-calendar-card-actions"
            >
              {hasHovered && (
                <>
                  {isForApproval && (
                    <Tooltip content={t('review')}>
                      <Button
                        onClick={handleEdit}
                        icon="edit_alt"
                        size={12}
                      />
                    </Tooltip>
                  )}
                  {isDraftOrScheduled && (
                    <Tooltip content={t('edit')}>
                      <Button
                        onClick={handleEdit}
                        icon="edit"
                        size={12}
                      />
                    </Tooltip>
                  )}
                  {isRejected && (
                    <Tooltip content={t('resubmit')}>
                      <Button
                        onClick={handleEdit}
                        icon="corner_down_left"
                        size={12}
                      />
                    </Tooltip>
                  )}
                  {isSentOrFailed && (
                    <Tooltip content={t('repost')}>
                      <Button
                        onClick={handleRepost}
                        icon="send"
                        size={12}
                      />
                    </Tooltip>
                  )}
                  <Tooltip content={t('delete')}>
                    <Button
                      onClick={() => setIsDeleteConfirmOpen(true)}
                      icon="trash"
                      size={12}
                    />
                  </Tooltip>
                </>
              )}
            </ActionsArea>
            <Tooltip
              noDecoration
              content={name}
              maxWidth
              delay={TOOLTIP_DELAY}
            >
              <WorkspaceAvatar
                $color={color}
                $isDark={colorTone === 'dark'}
                data-testid="list-calendar-card-workspace-avatar"
              >
                <span>{getAvatarInitials(name)}</span>
              </WorkspaceAvatar>
            </Tooltip>
          </TopRow>

          {title && <MessageText data-testid="list-calendar-card-message-text">{renderWithLinks(processTags(title))}</MessageText>}

          {campaigns && campaigns.length > 0 && (
            <CampaignsRow data-testid="list-calendar-card-campaigns">
              {campaigns.map((c) => (
                <CampaignTag
                  key={c.id}
                  $color={c.color}
                  $isDark={getColorTone(c.color) === 'dark'}
                  data-testid="list-calendar-card-campaign-tag"
                >
                  <span className="campaign-tag-avatar">{getAvatarInitials(c.name)}</span>
                  {c.name}
                </CampaignTag>
              ))}
            </CampaignsRow>
          )}

          {medias && medias.length > 0 && (
            <MediaRow data-testid="list-calendar-card-media-row">
              {medias.slice(0, 5).map((media, idx) => {
                const isPdf = isPdfMedia(media);
                const isFirst = idx === 0;
                const isLast = idx === 4;
                const overflowCount = medias.length - 5;
                const showOverlay = isLast && overflowCount > 0;

                return (
                  <MediaThumb
                    key={media.id}
                    data-testid="list-calendar-card-media-thumb"
                    onClick={() => setShowPreview(true)}
                  >
                    {isPdf ? (
                      <PdfThumbnail
                        url={media.url}
                        altText=""
                      />
                    ) : (
                      <>
                        {isFirst && isLoadingImage && (
                          <Skeleton
                            height="100%"
                            width="100%"
                          />
                        )}
                        <img
                          alt=""
                          src={media.type === 'Video' && media.thumbnailUrl ? media.thumbnailUrl : getCalendarThumbnailUrl(media, user)}
                          ref={isFirst ? imgRef : undefined}
                          loading="lazy"
                          decoding="async"
                          onLoad={() => isFirst && setIsLoadingImage(false)}
                          onError={() => isFirst && setIsLoadingImage(false)}
                          style={{ opacity: isFirst && isLoadingImage ? 0 : 1 }}
                        />
                        {(media.type === 'Video' || media.type === 'Document' || media.type === 'Gif' || media.isLinkPreview) && (
                          <ThumbHighlight>
                            {media.isLinkPreview ? (
                              <Icon
                                name="link"
                                color="brandLight"
                                strokeWidth={1.8}
                              />
                            ) : media.type === 'Gif' ? (
                              <span>GIF</span>
                            ) : (
                              <Icon
                                name={media.type === 'Video' ? 'play' : 'file'}
                                color="brandLight"
                                strokeWidth={1.8}
                              />
                            )}
                          </ThumbHighlight>
                        )}
                        {showOverlay && <ThumbOverflow>+{overflowCount + 1}</ThumbOverflow>}
                      </>
                    )}
                  </MediaThumb>
                );
              })}
            </MediaRow>
          )}
          {hasFailureReason && (
            <ErrorBox>
              <p>{failureReasonMessage}</p>
            </ErrorBox>
          )}
          {recipientsSection}
        </ContentArea>
      </Container>

      {showPreview && mediaList.length > 0 && (
        <MediaPreview
          initialMediaIndex={0}
          mediaList={mediaList}
          supportedActions={['download']}
          onActionClick={handleDownload}
          onClose={() => setShowPreview(false)}
          shouldShowActions
        />
      )}
    </>
  );
};
